import React, { useRef, useEffect, useState } from 'react';
import { OptimizedPhoto } from '../hooks/useOptimizedPhotos';
import { formatTrackingNumberWithMiddleEllipsis } from '../../../utils/textUtils';
import {CloseOutline} from "antd-mobile-icons";

/**
 * 懒加载图片组件
 * 使用 Intersection Observer 控制图片加载时机
 */
const LazyImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}> = ({ src, alt, className, onClick }) => {
  const imgRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // 加载后不再监听（避免反复加载）
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // 提前 200px 加载
        threshold: 0.01
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      className={`relative w-full h-full ${className || ''}`}
      onClick={onClick}
    >
      {isVisible ? (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
        />
      ) : (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
    </div>
  );
};

/**
 * 优化的图片列表组件
 * 使用纯 CSS 横向滚动 + Intersection Observer 懒加载
 * 不依赖 react-window，更简单且性能更好
 */
interface VirtualPhotoListProps {
  photos: OptimizedPhoto[];
  onPhotoClick: (photo: OptimizedPhoto) => void;
  onPhotoDelete: (photo: OptimizedPhoto) => void;
  onAddPhoto?: () => void;
  showAddButton?: boolean;
}

const VirtualPhotoList: React.FC<VirtualPhotoListProps> = ({
  photos,
  onPhotoClick,
  onPhotoDelete,
  onAddPhoto,
  showAddButton = false
}) => {
  if (photos.length === 0 && !showAddButton) {
    return null;
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="flex gap-2">
        {/* 添加按钮 */}
        {showAddButton && (
          <div
            className="relative aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors flex-shrink-0"
            style={{ width: 'calc((100vw - 48px) / 3)' }}
            onClick={onAddPhoto}
          >
            <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        )}

        {/* 图片列表 */}
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="flex flex-col flex-shrink-0"
            style={{ width: 'calc((100vw - 48px) / 3)' }}
          >
            {/* 图片容器 */}
            <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
              {/* 懒加载缩略图 */}
              <LazyImage
                src={photo.thumbnailUrl}
                alt={photo.trackingNumber || 'Photo'}
                className="cursor-pointer"
                onClick={() => onPhotoClick(photo)}
              />

              {/* 删除按钮 */}
              <div
                className="absolute top-1 right-1 bg-white text-black bg-opacity-60 rounded w-5 h-5 flex items-center justify-center text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onPhotoDelete(photo);
                }}
              >
                <CloseOutline fontSize={14}/>
              </div>
            </div>

            {/* 包裹号（显示在图片下方，仅 label 类型显示） */}
            {photo.trackingNumber && (
              <div className="text-xs text-gray-500 text-center mt-1 px-1">
                {formatTrackingNumberWithMiddleEllipsis(photo.trackingNumber)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VirtualPhotoList;
